import React from 'react';
import { CommunicationTrackingProvider } from './trackers/communicationTracker';
import { DocumentReferenceTrackingProvider } from './trackers/documentReferenceTracker';
import { ObservationTrackingProvider } from './trackers/observationTracker';
import { PatientTrackingProvider } from './trackers/patientTracker';
import { QuestionnaireResponseTrackingProvider } from './trackers/questionnaireResponseTracker';
import { UnknownTrackingProvider } from './trackers/unknownTracker';

type Props = {
  context: string;
  children: React.ReactNode;
};

type TrackerProvider = React.FC<Props>;

// Order is cosmetic: every provider scopes a sibling tracker, so the
// nesting order doesn't affect dedup behavior. Adding a tracker is a
// one-line append here.
const TRACKER_PROVIDERS: readonly TrackerProvider[] = [
  ObservationTrackingProvider,
  DocumentReferenceTrackingProvider,
  QuestionnaireResponseTrackingProvider,
  CommunicationTrackingProvider,
  PatientTrackingProvider,
  UnknownTrackingProvider,
];

/**
 * The convenience wrapper that "turns on" attachment dedup for an entire
 * report surface (Annual Report, Zori Report print view, ...).
 *
 * Each attachment type has its own tracker because each one decides "same
 * widget?" differently — biomarker observations dedupe by code, document
 * references dedupe by resource + page, and so on. Mounting them
 * individually at every site would be tedious and easy to forget, so this
 * component nests them all under one shared `context` prop.
 *
 * Practically: anywhere you render impression markdown that should not
 * repeat the same chart / PDF / questionnaire widget, wrap it in this
 * provider. Bump the `context` string when the user enters a meaningfully
 * new viewing session (new report opened, edit mode toggled, filter
 * changed) so dedup state resets cleanly.
 */
export const CombinedRenderingTrackingProvider: React.FC<Props> = ({
  context,
  children,
}) => (
  <>
    {TRACKER_PROVIDERS.reduceRight<React.ReactNode>(
      (tree, Provider) => (
        <Provider context={context}>{tree}</Provider>
      ),
      children,
    )}
  </>
);
